import { inject } from '@angular/core';
import { type CanActivateFn, Router } from '@angular/router';
import { BrandingService } from '@services/branding/branding.service';
import { catchError, map, of } from 'rxjs';

/**
 * Guard factory that checks if a feature flag is enabled for the current branding.
 * Redirects to /login if the feature is disabled.
 *
 * Usage in routes:
 * {
 *   path: '',
 *   component: LandingComponent,
 *   canActivate: [featureFlagGuard('landingPage')],
 * }
 */
export function featureFlagGuard(flag: string, redirectTo = '/login'): CanActivateFn {
	return () => {
		const brandingService = inject(BrandingService);
		const router = inject(Router);

		// If branding is already loaded, check synchronously
		const branding = brandingService.current;
		if (branding) {
			const isEnabled = branding.featureFlags?.[flag] ?? false;
			if (isEnabled) {
				return true;
			}
			return router.parseUrl(redirectTo);
		}

		// Otherwise, load branding first and then check
		return brandingService.load().pipe(
			map((loadedBranding) => {
				const isEnabled = loadedBranding.featureFlags?.[flag] ?? false;
				if (isEnabled) {
					return true;
				}
				return router.parseUrl(redirectTo);
			}),
			catchError(() => {
				return of(router.parseUrl(redirectTo));
			}),
		);
	};
}
